<template>
  <section>
    <h2 class="h3 mb-3">Draft Details</h2>
    <div class="f-form">

      <div class="f-form__half">
        <f-text
          v-if="edit"
          id="draftName"
          label="Draft Name"
          :value="value.name"
          required
          :error="e('name')"
          @input="update('name', $event)"
        />
        <f-search-select
          v-else
          id="draftName"
          label="Draft Name"
          :value="value.draft_id"
          :options="availableDrafts"
          track-by="id"
          :custom-label="draftOptionLabel"
          placeholder="Select draft"
          :loading="loadingDrafts"
          required
          :error="e('draft_id')"
          @input="update('draft_id', $event)"
          @select="onDraftSelect"
        />
      </div>

      <div class="f-form__pair">
        <f-text
          id="nomsOpen"
          label="Noms Open"
          type="datetime-local"
          :value="value.noms_open"
          required
          :error="e('noms_open')"
          @input="update('noms_open', $event)"
        />
        <f-text
          id="nomsClosed"
          label="Noms Closed"
          type="datetime-local"
          :value="value.noms_closed"
          :error="e('noms_closed')"
          @input="update('noms_closed', $event)"
        />
      </div>

      <div class="f-form__pair">
        <f-text
          id="startDate"
          label="Start Date"
          type="date"
          :value="value.start_date"
          required
          :error="e('start_date')"
          @input="update('start_date', $event)"
        />
        <f-text
          id="finishDate"
          label="Finish Date"
          type="date"
          :value="value.finish_date"
          required
          :error="e('finish_date')"
          @input="update('finish_date', $event)"
        />
      </div>

      <div class="f-form__pair">
        <f-search-select
          id="status"
          label="Status"
          :value="value.draft_state_id"
          :options="states"
          :option-tooltip="state => state.description || ''"
          :loading="loadingStates"
          :searchable="false"
          required
          :error="e('draft_state_id')"
          @input="update('draft_state_id', $event)"
        />
        <f-text
          id="intervalNum"
          label="Spacings"
          type="number"
          :value="value.interval_num"
          placeholder="50"
          :error="e('interval_num')"
          @input="update('interval_num', $event)"
        />
      </div>

      <div class="f-form__pair">
        <f-text
          id="secretaryContactName"
          label="Secretary Contact Name"
          :value="value.secretary_contact_name"
          required
          :error="e('secretary_contact_name')"
          @input="update('secretary_contact_name', $event)"
        />
        <f-text
          id="secretaryPhoneNumber"
          label="Secretary Phone Number"
          :value="value.secretary_phone_number"
          required
          :error="e('secretary_phone_number')"
          @input="update('secretary_phone_number', $event)"
        />
      </div>

      <div class="f-form__full secretary-picker-row">
        <committee-secretary-picker @select="pickSecretary" />
      </div>

      <div class="f-form__half">
        <f-checkbox
          id="isFinal"
          label="National Finals"
          :value="value.is_final"
          @input="update('is_final', $event)"
        />
      </div>

      <div class="f-form__pair">
        <f-text
          id="stockLevy"
          label="Stock Levy"
          :value="value.stock_levy"
          placeholder="20.00"
          @input="update('stock_levy', $event)"
        />
      </div>

      <div class="f-form__half f-form__half--lg">
        <f-textarea
          id="draftNotes"
          label="Draft Notes"
          :value="value.draft_notes"
          :rows="3"
          @input="update('draft_notes', $event)"
        />
      </div>

      <div class="f-form__pair">
        <f-checkbox
          id="eventMembersAllowed"
          label="Allow event members to participate"
          :value="value.event_members_allowed"
          :lock-if-true="true"
          hint="Once this option is selected and saved, it cannot be undone."
          @input="update('event_members_allowed', $event)"
        />
      </div>

      <div class="f-form__half">
        <f-checkbox
          id="isShowBiosecurityForm"
          label="Add a Biosecurity Form to the Draft"
          :value="value.is_show_biosecurity_form"
          @input="update('is_show_biosecurity_form', $event)"
        />
      </div>

      <div class="f-form__pair">
        <f-checkbox
          id="requiredVehicles"
          label="Make competitor vehicle mandatory"
          :value="value.required_vehicles"
          @input="update('required_vehicles', $event)"
        />
        <div />
      </div>

    </div>
  </section>
</template>

<script>
import FText from '@/components/FormFields/FText'
import FCheckbox from '@/components/FormFields/FCheckbox'
import FTextarea from '@/components/FormFields/FTextarea'
import FSearchSelect from '@/components/FormFields/FSearchSelect'
import CommitteeSecretaryPicker from '@/components/Drafts/CommitteeSecretaryPicker'

// "YYYY-MM-DDTHH:mm" (datetime-local) → same format, shifted +24h
function plus24Hours(val) {
  const d = new Date(val)
  if (Number.isNaN(d.getTime())) return ''
  d.setHours(d.getHours() + 24)
  const pad = n => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`
}

export default {
  name: 'DraftDetailsSection',
  components: { FText, FCheckbox, FTextarea, FSearchSelect, CommitteeSecretaryPicker },
  props: {
    value: { type: Object, required: true },
    edit: Boolean,
    availableDrafts: { type: Array, default: () => [] },
    loadingDrafts: { type: Boolean, default: false },
    states: { type: Array, default: () => [] },
    loadingStates: { type: Boolean, default: false },
    errors: { type: Object, default: () => ({}) }
  },
  methods: {
    e(field) {
      return this.errors[field] ? this.errors[field][0] : null
    },
    draftOptionLabel(draft) {
      return draft.start_date ? `${draft.start_date} ${draft.name}` : draft.name
    },
    update(field, val) {
      // Monolith parity: picking Noms Open defaults Noms Closed to open + 24h.
      if (field === 'noms_open' && val) {
        this.$emit('input', { ...this.value, noms_open: val, noms_closed: plus24Hours(val) })
        return
      }
      this.$emit('input', { ...this.value, [field]: val })
    },
    pickSecretary(secretary) {
      this.$emit('input', {
        ...this.value,
        secretary_contact_name: secretary.name,
        secretary_phone_number: secretary.phone
      })
    },
    onDraftSelect(draft) {
      if (!draft) return
      this.$emit('input', {
        ...this.value,
        name: draft.name,
        draft_id: draft.id,
        start_date: draft.start_date || this.value.start_date,
        finish_date: draft.end_date || this.value.finish_date,
        is_national_finals: draft.is_national_finals != null ? draft.is_national_finals : this.value.is_national_finals,
        event_members_allowed: draft.event_members_allowed != null ? draft.event_members_allowed : this.value.event_members_allowed
      })
    }
  }
}
</script>

<style scoped>
/* Sits right under the Secretary Contact fields instead of a full grid row-gap below them */
.secretary-picker-row {
  margin-top: -1rem;
}
</style>



// WEBPACK FOOTER //
// src/components/Drafts/DraftDetailsSection.vue